import { Location } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { LinkAccount, SessionState, SignOut } from '@platform-frontend/account';
import { AssetState, FetchAssets, SetCheckoutSessionRequired, VendorState } from '@platform-frontend/checkout';
import {
  CheckoutEnvironment,
  CheckoutFrameCommunicationMessage,
  InitMessage,
  NotifyAssetStatusChangeMessage,
  NotifyCheckoutSessionChangeMessage,
  NotifySignedInChangeMessage,
  RedirectMessage,
  RegisterForAssetStatusChangeMessage,
  RegisterForCheckoutSessionChangeMessage,
  SetFrameVisibilityMessage,
  sanitizeProductId,
  sanitizeVendorId
} from '@platform-frontend/checkout-drop-in';
import { ProductIdentifier } from '@platform-frontend/marketplace';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import {
  AutostartPageService,
  CloseWindow,
  GotoError,
  InvalidStateError,
  Navigate,
  NavigateByUrl,
  SetGuardsActivated,
  SimpleRouterState,
  StorageService,
  WINDOW,
  WindowParentService,
  deepEqual,
  filterTrue,
  handleAllErrors,
  tapNextOrError
} from '@platform-frontend/util';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, concat, defer, fromEvent, of, throwError } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  skip,
  startWith,
  switchMap,
  take,
  takeLast,
  takeUntil,
  takeWhile,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { AppRoutesService } from '../../app-routes.service';
import { ENVIRONMENT } from '../../environment';
import { AutostartService } from '../autostart.service';
import { CheckoutVendorType } from '../model/checkout-vendor-type';
import { IIntegrationService } from '../web-checkout-integration.service';
import { WebCheckoutStartParamsSanitizerService } from '../web-checkout-start-params-sanitizer.service';
import { WebCheckoutStartupRedirectionService } from '../web-checkout-startup-redirection.service';
import { ClearHistoryHandling } from './model/clear-history-handling';

@Injectable({
  providedIn: 'root'
})
export class HostService implements IIntegrationService {
  constructor(
    private actions$: Actions,
    private appRoutesService: AppRoutesService,
    private autostartService: AutostartService,
    private autostartPageService: AutostartPageService,
    private location: Location,
    private router: Router,
    private storageService: StorageService,
    private store: Store,
    private webCheckoutStartParamsSanitizerService: WebCheckoutStartParamsSanitizerService,
    private webCheckoutStartupRedirectionService: WebCheckoutStartupRedirectionService,
    @Inject(WINDOW) private window: Window,
    private windowParentService: WindowParentService
  ) {}

  private hostUrl: string | null = null;
  private hostIdentified$: ReplaySubject<void> = new ReplaySubject<void>(1);
  private initMessageHandled: boolean = false;
  private isIFrameOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isNavigatingBackToStartup$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private registeredProductsForAssetStatus: ProductIdentifier<CheckoutEnvironment>[] = [];
  private registeredProductForAssetStatus$: ReplaySubject<void> = new ReplaySubject<void>(1);
  private registeredVendorForCheckoutSession: string | null = null;
  private registeredVendorForCheckoutSession$: ReplaySubject<void> = new ReplaySubject<void>(1);
  private queuedHostMessages: CheckoutFrameCommunicationMessage[] = [];

  private readonly STORAGE_URL_KEY: string = 'yattaCheckoutUrl';

  init(): void {
    this.windowParentService.init();
    this.fetchAssetsWhenRequired().subscribe();
    this.setCheckoutSessionRequired().subscribe();
    this.registerForHostMessages().subscribe();
    this.notifyHostOnSignedInChange().subscribe();
    this.notifyHostOnCheckoutSessionChange().subscribe();
    this.closeFrameOrStoreUrlOnNavigations().subscribe();
    this.closeFrameOnCloseWindow().subscribe();
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Section 1: Host identification
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  getHostUrl(): string {
    return this.hostUrl ?? '';
  }

  getVendorType(): CheckoutVendorType {
    if (this.isYattaHost()) {
      return 'YATTA';
    } else if (this.isUmlLabHost()) {
      return 'UML_LAB';
    }
    return 'EXTERNAL';
  }

  private isYattaHost(): boolean {
    return this.checkHostUrl(
      (hostName, path) => hostName.endsWith('.' + PLATFORM_ENVIRONMENT.rootUrl.replace('https://', '')) && !path.startsWith('/products/')
    );
  }

  private isUmlLabHost(): boolean {
    return this.checkHostUrl(hostName => hostName.endsWith('.' + ENVIRONMENT.umlLabUrl.replace('https://', '')));
  }

  private checkHostUrl(condition: (hostName: string, path: string) => boolean): boolean {
    const regexp = /^https?:\/\/([^/:]*)(:\d+)?(\/?.*)/g;

    if (this.hostUrl !== null) {
      const array = [...this.hostUrl.matchAll(regexp)];
      const host = array.map(m => m[1])[0];
      const port = array.map(m => m[2])[0];
      const path = array.map(m => m[3])[0];
      const hostName = '.' + host + (port ?? '');
      return condition(hostName, path);
    }
    return false;
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Section 2: Handle messages from host
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private registerForHostMessages(): Observable<unknown> {
    return fromEvent<MessageEvent>(this.window, 'message').pipe(
      filter(event => this.isCheckoutFrameCommunicationMessageEvent(event)),
      map(event => event.data),
      filter(message => this.proceedOnMessageOrHandleItIndependently(message)),
      concatMap(message => this.handleOrQueueHostMessage(message))
    );
  }

  private isCheckoutFrameCommunicationMessageEvent(event: MessageEvent): event is MessageEvent<CheckoutFrameCommunicationMessage> {
    return this.windowParentService.isOriginHost(event.origin) && event.data.source === 'checkout-drop-in';
  }

  private proceedOnMessageOrHandleItIndependently(message: CheckoutFrameCommunicationMessage): boolean {
    if (this.canMessageBeHandledIndependently(message.action)) {
      this.handleHostMessageAndCatchError(message, false).subscribe();
      return false;
    }
    return true;
  }

  private canMessageBeHandledIndependently(action: CheckoutFrameCommunicationMessage['action']): boolean {
    return (
      action === 'setFrameVisibilityConfirm' ||
      action === 'linkAccount' ||
      action === 'registerForAssetStatusChange' ||
      action === 'registerForCheckoutSessionChange'
    );
  }

  private handleOrQueueHostMessage(message: CheckoutFrameCommunicationMessage): Observable<unknown> {
    return !this.initMessageHandled && message.action !== 'init' ? this.queueHostMessage(message) : this.handleHostMessageAndProcessQueue(message);
  }

  private queueHostMessage(message: CheckoutFrameCommunicationMessage): Observable<unknown> {
    this.queuedHostMessages.push(message);
    return of(null);
  }

  private handleHostMessageAndProcessQueue(message: CheckoutFrameCommunicationMessage): Observable<unknown> {
    return this.handleHostMessageAndCatchError(message, true).pipe(
      filter(() => message.action === 'init' || this.initMessageHandled),
      map(() => this.queuedHostMessages),
      tap(() => (this.queuedHostMessages = [])),
      tap(() => (this.initMessageHandled = true)),
      switchMap(queuedHostMessages => concat(queuedHostMessages.map(message => this.handleHostMessageAndCatchError(message, true))))
    );
  }

  private handleHostMessageAndCatchError(
    message: CheckoutFrameCommunicationMessage,
    closeFrameOnError: boolean
  ): Observable<CheckoutFrameCommunicationMessage> {
    return this.handleHostMessage(message).pipe(
      catchError(() => (closeFrameOnError ? this.setFrameVisibility(false) : of(null))),
      map(() => message)
    );
  }

  private handleHostMessage(message: CheckoutFrameCommunicationMessage): Observable<unknown> {
    switch (message.action) {
      case 'init':
        return this.handleInitMessage(message);
      case 'linkAccount':
        return this.store.dispatch(
          new LinkAccount(this.webCheckoutStartParamsSanitizerService.sanitizeLinkedAccount(message.linkedAccount), this.windowParentService.getHostDomain())
        );
      case 'registerForAssetStatusChange':
        return this.registerForAssetStatusChange(message);
      case 'registerForCheckoutSessionChange':
        return this.registerForCheckoutSessionChange(message);
      case 'setFrameVisibilityConfirm':
        this.isIFrameOpen$.next(message.visible);
        return of(null);
      case 'showError':
        return this.openFrameAndDispatchNavigation(new GotoError({ queryParams: { errorType: message.errorType } }));
      case 'signIn':
        return this.webCheckoutStartupRedirectionService.signIn(
          navAction => this.openFrameAndDispatchNavigation(navAction),
          this.webCheckoutStartParamsSanitizerService.sanitizeSignInParams({ vendorId: message.vendorId, ...message.params })
        );
      case 'signOut':
        return this.store.dispatch(new SignOut());
      case 'startAcademicRegistration':
        return this.webCheckoutStartupRedirectionService.startAcademicRegistration(
          navAction => this.openFrameAndDispatchNavigation(navAction),
          this.webCheckoutStartParamsSanitizerService.sanitizeAcademicParams(message.params)
        );
      case 'startCheckout':
        return this.webCheckoutStartupRedirectionService.startCheckout(
          navAction => this.openFrameAndDispatchNavigation(navAction),
          this.webCheckoutStartParamsSanitizerService.sanitizeCheckoutParams({ productId: message.productId, ...message.params })
        );
      default:
        return throwError(() => new InvalidStateError(`There is no flow matching the following host action: ${message.action}.`));
    }
  }

  private handleInitMessage(message: InitMessage): Observable<unknown> {
    if (this.hostUrl !== null) {
      return throwError(() => new InvalidStateError('Init message already handled!'));
    }
    this.hostUrl = this.windowParentService.getHostUrl(message.hostRelativeRef);
    this.hostIdentified$.next();

    return this.handleInitAutoStart(message.autostartRef, message.isReload);
  }

  private handleInitAutoStart(autostartRef: string | null, isReload: boolean): Observable<unknown> {
    if (autostartRef !== null) {
      return this.setFrameVisibility(true, true).pipe(switchMap(() => this.autostartService.performAutostart(autostartRef)));
    }

    /**
     * Please note that Chromium based browsers (in contrast to Firefox) after refreshing the page with F5 keep the iframe history in the browser history.
     * This leads to a corrupted state when navigating back in the browser after the refresh. See:
     * https://www.sitepoint.com/community/t/browser-back-button-behavior-before-and-after-refresh/310300
     */
    const reloadUrl = this.getStorageUrlKey();
    this.clearStorageUrlKey();
    return isReload && reloadUrl ? this.openFrameAndDispatchNavigation(new NavigateByUrl(reloadUrl)) : of(null);
  }

  private openFrameAndDispatchNavigation(navigationAction: unknown): Observable<unknown> {
    return this.isIFrameOpen$.pipe(
      take(1),
      switchMap(isIFrameOpen => (isIFrameOpen ? throwError(() => new InvalidStateError('Frame already started!')) : this.setFrameVisibility(true))),
      switchMap(() => this.setGuardsActivatedAndDispatchNavigation(navigationAction))
    );
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Section 3: Callback api to host
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  getRegisteredProductsForAssetStatus(): ProductIdentifier<CheckoutEnvironment>[] {
    return this.registeredProductsForAssetStatus;
  }

  getRegisteredVendorForCheckoutSession(): string | null {
    return this.registeredVendorForCheckoutSession;
  }

  private registerForAssetStatusChange(message: RegisterForAssetStatusChangeMessage): Observable<unknown> {
    const productIdentifier: ProductIdentifier<CheckoutEnvironment> = {
      productId: sanitizeProductId(message.productId),
      environment: 'environment' in message ? message.environment : 'PREVIEW'
    };
    if (!this.registeredProductsForAssetStatus.some(prodId => deepEqual(prodId, productIdentifier))) {
      this.registeredProductsForAssetStatus.push(productIdentifier);
      this.registeredProductForAssetStatus$.next();
      return this.notifyHostOnAssetStatusChange(productIdentifier);
    }
    this.registeredProductForAssetStatus$.next();
    return of(null);
  }

  private fetchAssetsWhenRequired(): Observable<unknown> {
    return combineLatest([this.hostIdentified$.pipe(take(1)), this.registeredProductForAssetStatus$]).pipe(
      switchMap(() => this.store.selectOnce(SessionState.isSignedIn)),
      switchMap(isSignedIn => (isSignedIn ? this.store.dispatch(new FetchAssets()).pipe(catchError(() => of(null))) : of(null)))
    );
  }

  private registerForCheckoutSessionChange(message: RegisterForCheckoutSessionChangeMessage): Observable<unknown> {
    const vendorId = sanitizeVendorId(message.vendorId);
    if (!deepEqual(this.registeredVendorForCheckoutSession, vendorId)) {
      this.registeredVendorForCheckoutSession = vendorId;
      this.registeredVendorForCheckoutSession$.next();
    }
    this.registeredVendorForCheckoutSession$.next();
    return of(null);
  }

  private setCheckoutSessionRequired(): Observable<unknown> {
    return combineLatest([this.hostIdentified$, this.registeredVendorForCheckoutSession$]).pipe(
      take(1),
      switchMap(() => this.store.dispatch(new SetCheckoutSessionRequired()))
    );
  }

  private notifyHostOnSignedInChange(): Observable<unknown> {
    return this.store.select(SessionState.isSignedIn).pipe(
      distinctUntilChanged(),
      map(
        (isSignedIn): NotifySignedInChangeMessage => ({
          source: 'web-checkout',
          action: 'notifySignedInChange',
          signedIn: isSignedIn
        })
      ),
      tap(message => this.windowParentService.postMessage(message))
    );
  }

  private notifyHostOnCheckoutSessionChange(): Observable<unknown> {
    return combineLatest([this.store.select(SessionState.isSignedIn), this.store.select(VendorState.checkoutSession)]).pipe(
      filter(([isSignedIn, checkoutSession]) => isSignedIn === (checkoutSession !== null)),
      map(([, checkoutSession]) => checkoutSession),
      distinctUntilChanged((oldCheckoutSession, newCheckoutSession) => deepEqual(oldCheckoutSession, newCheckoutSession)),
      map(
        (checkoutSession): NotifyCheckoutSessionChangeMessage => ({
          source: 'web-checkout',
          action: 'notifyCheckoutSessionChange',
          checkoutSession: checkoutSession
        })
      ),
      tap(message => this.windowParentService.postMessage(message))
    );
  }

  private notifyHostOnAssetStatusChange(productIdentifier: ProductIdentifier<CheckoutEnvironment>): Observable<unknown> {
    return this.store.select(AssetState.assetStatusWithProduct).pipe(
      map(
        assetStatusWithProduct =>
          assetStatusWithProduct
            .filter(
              assetStatusWithProduct =>
                deepEqual(assetStatusWithProduct.product.productIdentifier, productIdentifier) ||
                (assetStatusWithProduct.product.productIdentifier.productId === productIdentifier.productId &&
                  assetStatusWithProduct.product.publishState === 'NOT_PUBLISHED')
            )
            .map(assetStatusWithProduct => assetStatusWithProduct.assetStatus) ?? null
      ),
      distinctUntilChanged((oldStatus, newStatus) => deepEqual(oldStatus, newStatus)),
      map(
        (assetStatus): NotifyAssetStatusChangeMessage => ({
          source: 'web-checkout',
          action: 'notifyAssetStatusChange',
          ...productIdentifier,
          assetStatus: {
            assetStatus,
            // Deprecated status field, to be removed in the future. This is only present for backward compatibility with the old API.
            status: assetStatus.map(a => (a.type === 'SUBSCRIPTION' ? { ...a, bookingStatus: a.bookingStatus === 'EXPIRING' ? 'CANCELED' : 'ONGOING' } : a))
          }
        })
      ),
      tap(message => this.windowParentService.postMessage(message))
    );
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Section 4: Closing the frame
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private closeFrameOnCloseWindow(): Observable<unknown> {
    return this.actions$.pipe(
      ofActionDispatched(CloseWindow),
      this.filterIframeOpenAndNotRestartingOrClearingHistory,
      switchMap(() => this.closeFrame())
    );
  }

  private closeFrameOrStoreUrlOnNavigations(): Observable<unknown> {
    return this.router.events.pipe(
      filter((e): e is NavigationStart => e instanceof NavigationStart),
      this.filterIframeOpenAndNotRestartingOrClearingHistory,
      switchMap(e =>
        this.isUrlEmpty(e.url) ? this.closeFrame(e.navigationTrigger === 'imperative' ? 'default' : 'clearOnlyForwardHistory') : this.storeUrl(e.url)
      )
    );
  }

  private closeFrame(clearHistoryHandling: ClearHistoryHandling = 'default'): Observable<unknown> {
    const closedFromConfirmationPage = this.store.selectSnapshot(SimpleRouterState.data).isSuccessPage;
    return this.isIFrameOpen$.pipe(
      take(1),
      filterTrue(),
      switchMap(() => this.setFrameVisibility(false)),
      switchMap(() => this.clearHistory(clearHistoryHandling === 'default')),
      tapNextOrError(() => this.clearStorageUrlKey()),
      tapNextOrError(() => this.sendRedirectMessageIfNecessary(closedFromConfirmationPage))
    );
  }

  private sendRedirectMessageIfNecessary(closedFromConfirmationPage: boolean): void {
    const redirectUrl = this.webCheckoutStartupRedirectionService.getCloseWindowRedirectUrl(closedFromConfirmationPage);
    if (redirectUrl) {
      this.windowParentService.postMessage<RedirectMessage>({ source: 'web-checkout', action: 'redirect', redirectUrl });
    }
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Section 5: Restarting the frame
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  restart(restartUrl?: string): Observable<unknown> {
    const url: string | null = restartUrl ?? this.getStorageUrlKey();
    return this.navigateBackToStartup().pipe(
      switchMap(() => (url ? this.setGuardsActivatedAndDispatchNavigation(new NavigateByUrl(url)) : this.closeFrame('clearOnlyForwardHistory')))
    );
  }

  private clearStorageUrlKey(): void {
    this.storageService.removeFromSessionStorage(this.STORAGE_URL_KEY);
  }

  private getStorageUrlKey(): string | null {
    return this.storageService.getPlainFromSessionStorage(this.STORAGE_URL_KEY);
  }

  private storeUrl(url: string): Observable<unknown> {
    this.storageService.putInSessionStorage(this.STORAGE_URL_KEY, this.appRoutesService.adjustToFlowStartupUrl(url));
    return of(null);
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Section 6: Brwoser history handling
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  isRestartingOrShuttingDown(): Observable<boolean> {
    return this.isNavigatingBackToStartup$.asObservable();
  }

  private filterIframeOpenAndNotRestartingOrClearingHistory = <T>(obs: Observable<T>): Observable<T> =>
    obs.pipe(
      withLatestFrom(this.isIFrameOpen$, this.isNavigatingBackToStartup$),
      filter(([, isIFrameOpen, isNavigatingBackToStartup]) => isIFrameOpen && !isNavigatingBackToStartup),
      map(([e]) => e)
    );

  /**
   * Navigate backwards until the current route is /empty and clear the forward history.
   * @param allowBackNavigations Must be set to false if the current url might already be the initial /empty route. In this case only the forward history will be cleared.
   */
  private clearHistory(allowBackNavigations: boolean): Observable<unknown> {
    if (allowBackNavigations) {
      return concat(this.navigateBackToStartup(), this.clearForwardHistory());
    }
    return this.clearForwardHistory();
  }

  /**
   * Navigate backwards until the current route is /empty, which is always the first history entry inside the iframe.
   * ATTENTION: Works only as long as not the same route (with the same query params) is present multiple times side-by-side in the browser history!!!
   */
  private navigateBackToStartup(): Observable<unknown> {
    const popstateEvents$ = this.router.events.pipe(filter((e): e is NavigationStart => e instanceof NavigationStart && e.navigationTrigger === 'popstate'));
    this.isNavigatingBackToStartup$.next(true);

    return this.store.dispatch(new SetGuardsActivated(false)).pipe(
      switchMap(() => popstateEvents$),
      startWith(null),
      takeWhile(() => !this.autostartPageService.isAutostartPage(), true),
      switchMap(() => this.navigateBackDependingOnHistoryState()),
      takeUntil(popstateEvents$.pipe(filter(e => this.isUrlEmpty(e.url)))),
      takeUntil(this.isIFrameOpen$.pipe(skip(1), filterTrue())),
      takeLast(1),
      switchMap(() => this.store.dispatch(new SetGuardsActivated(true))),
      handleAllErrors(new SetGuardsActivated(true)),
      tapNextOrError(() => this.isNavigatingBackToStartup$.next(false))
    );
  }

  private navigateBackDependingOnHistoryState(): Observable<unknown> {
    if (!this.autostartPageService.isAutostartPage()) {
      this.location.back();
      return of(null);
    }
    return this.store.dispatch(new Navigate(['/empty'], { replaceUrl: true }));
  }

  /**
   * Navigate forward to /back-to-empty to clear the forward history.
   * A guard on that route will automatically redirect back.
   */
  private clearForwardHistory(): Observable<unknown> {
    return defer(() => this.store.dispatch(new Navigate(['/back-to-empty'])));
  }

  private isUrlEmpty(url: string): boolean {
    return url.startsWith('/empty');
  }

  private setGuardsActivatedAndDispatchNavigation(navigationAction: unknown): Observable<unknown> {
    return this.store.dispatch(new SetGuardsActivated(true)).pipe(switchMap(() => this.store.dispatch(navigationAction)));
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Section 7: IframeVisibility
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  isApplicationVisible(): Observable<boolean> {
    return this.isIFrameOpen$.asObservable();
  }

  private setFrameVisibility(visible: boolean, clearAutostartParam: boolean = false): Observable<unknown> {
    return this.isIFrameOpen$.pipe(
      distinctUntilChanged(),
      tap(open => this.setFrameVisibilityIfNecessary(visible, clearAutostartParam, open)),
      filter(open => open === visible),
      take(1)
    );
  }

  private setFrameVisibilityIfNecessary(visible: boolean, clearAutostartParam: boolean, currentlyVisible: boolean): void {
    if (currentlyVisible !== visible)
      this.windowParentService.postMessage<SetFrameVisibilityMessage>({
        source: 'web-checkout',
        action: 'setFrameVisibility',
        visible,
        clearAutostartParam
      });
  }
}
